/**
 * AI Predictions API Service.
 *
 * Provides methods for interacting with the AI Predictions backend at `https://api.hydry.de`.
 *
 * **Every call goes through the `assistApiProxy` Cloud Function, never to ASSIST directly.** ASSIST
 * authenticates with a single shared `X-API-Key` that it accepts on every one of its routes, so a
 * browser that holds it can read any client's predictions and start backfill jobs on the VPS. A
 * credential like that cannot be restricted to an origin, so it stays in Cloud and the browser
 * authenticates as itself (Firebase ID token) instead. The proxy also enforces the IRES-only gate
 * server-side, which `Capability.VIEW_AI_PREDICTIONS` only enforces in the UI.
 *
 * Usage:
 * ```ts
 * import { AiPredictionApi } from "@/api/AiPredictionApi";
 *
 * const predictions = await AiPredictionApi.getPredictions(chainId, serialNo);
 * ```
 *
 * Reference: docs/devices/ai-predictions/backend-requirements.md
 */
import { functions } from "@/main";
import {
  ManualPredictRequest,
  ManualPredictResponse,
  PredictionsResponse,
  TrendResponse,
  SaveCorrectionRequest,
  SaveCorrectionResponse,
  CorrectionDocument,
  BackfillRequest,
  BackfillResponse,
  ActivationBackfillRequest,
  ActivationBackfillResponse,
} from "@/data/models/AiPrediction";

// ─── Configuration ───────────────────────────────────────────────────────────

/**
 * The Cloud Function that fronts ASSIST.
 *
 * Swap to `assistApiProxyDevelopment` **locally only** to drive the development twin from a dev
 * server without touching the function released clients call.
 */
const ASSIST_PROXY_CALLABLE = "assistApiProxy";

/** Matches the proxy's own outbound timeout, plus room for the Cloud round trip. */
const DEFAULT_TIMEOUT_MS = 35000;

/** Manual predictions run the model over every window; ASSIST documents 5-15s. */
const MANUAL_PREDICTION_TIMEOUT_MS = 65000;

// ─── Error Handling ──────────────────────────────────────────────────────────

/**
 * Custom error class for AI Prediction API errors.
 * Preserves the API's error message and HTTP status code.
 */
export class AiPredictionApiError extends Error {
  public readonly statusCode: number;
  public readonly apiMessage: string;

  constructor(statusCode: number, apiMessage: string) {
    super(`AI Prediction API error (${statusCode}): ${apiMessage}`);
    this.name = "AiPredictionApiError";
    this.statusCode = statusCode;
    this.apiMessage = apiMessage;
  }
}

/**
 * HTTP status for each callable error code the proxy can raise.
 *
 * The proxy returns ASSIST's own failures as data so their status survives; this table only covers
 * the failures Cloud itself raises (rejected by the gate, malformed request, transport). Callers only
 * catch `AiPredictionApiError`, so a raw `FirebaseError` reaching them would fall through every
 * handler in the four components that use this client.
 */
const CALLABLE_ERROR_STATUS: Record<string, number> = {
  "invalid-argument": 400,
  unauthenticated: 401,
  "failed-precondition": 401,
  "permission-denied": 403,
  "not-found": 404,
  "deadline-exceeded": 504,
  unavailable: 503,
};

/** Wraps anything the callable throws as the error type the callers already handle. */
function toAiPredictionApiError(error: unknown): AiPredictionApiError {
  const code = (error as { code?: string })?.code ?? "";
  const message =
    (error as { message?: string })?.message ?? "Unknown API error";

  return new AiPredictionApiError(CALLABLE_ERROR_STATUS[code] ?? 500, message);
}

// ─── Transport ───────────────────────────────────────────────────────────────

/** One allowlisted operation on the proxy, with the arguments that operation addresses by. */
interface AssistProxyRequest {
  operation: string;
  chainId?: string;
  serialNo?: string;
  jobId?: string;
  days?: number;
  modelVersion?: string;
  body?: unknown;
}

/** What the proxy resolves with: ASSIST's payload, or ASSIST's failure with its status intact. */
interface AssistProxyResult<T> {
  ok?: boolean;
  data?: T;
  statusCode?: number;
  apiMessage?: string;
}

/**
 * Calls the proxy and unwraps its result.
 *
 * @throws {AiPredictionApiError} for an ASSIST failure, a rejected caller, or a transport error —
 *   always this type, so callers need one catch.
 */
async function callAssist<T>(
  request: AssistProxyRequest,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<T> {
  let result: AssistProxyResult<T>;

  try {
    const callable = functions.httpsCallable(ASSIST_PROXY_CALLABLE, {
      timeout: timeoutMs,
    });
    result = (await callable(request)).data as AssistProxyResult<T>;
  } catch (error) {
    throw toAiPredictionApiError(error);
  }

  if (result?.ok !== true) {
    throw new AiPredictionApiError(
      result?.statusCode ?? 500,
      result?.apiMessage ?? "Unknown API error"
    );
  }

  return result.data as T;
}

// ─── API Methods ─────────────────────────────────────────────────────────────

/**
 * AI Predictions API client.
 *
 * All methods are static — no instantiation needed. Errors throw
 * `AiPredictionApiError` with the HTTP status code and API error message.
 */
export const AiPredictionApi = {
  /**
   * Load all prediction data for the Quality page.
   *
   * Returns the latest daily result, manual result, correction, and chain
   * summary in a single call. Uses O(1) targeted Firestore reads on the backend.
   *
   * @param chainId - The program chain ID (e.g., "BB005_19-24:07:2025_11:33:45")
   * @param serialNo - ComBox serial number (e.g., "BB005")
   * @returns Combined predictions, corrections, and summary
   *
   * @throws {AiPredictionApiError} 400 — Missing serialNo
   * @throws {AiPredictionApiError} 403 — Caller is not an IRES user
   * @throws {AiPredictionApiError} 404 — Chain not found
   */
  async getPredictions(
    chainId: string,
    serialNo: string
  ): Promise<PredictionsResponse> {
    return callAssist<PredictionsResponse>({
      operation: "getPredictions",
      chainId,
      serialNo,
    });
  },

  /**
   * Get daily prediction history for the trend chart.
   *
   * Returns one lightweight summary entry per daily prediction,
   * most-recent first.
   *
   * @param chainId - The program chain ID
   * @param serialNo - ComBox serial number
   * @param days - Number of most-recent daily results (default: 30, max: 365)
   * @param modelVersion - Optional RENO model version filter (e.g. "R1.0")
   * @returns Trend data with daily summaries
   *
   * @throws {AiPredictionApiError} 400 — Missing serialNo or invalid days
   * @throws {AiPredictionApiError} 403 — Caller is not an IRES user
   * @throws {AiPredictionApiError} 404 — Chain not found
   */
  async getTrend(
    chainId: string,
    serialNo: string,
    days = 30,
    modelVersion?: string
  ): Promise<TrendResponse> {
    return callAssist<TrendResponse>({
      operation: "getTrend",
      chainId,
      serialNo,
      days,
      ...(modelVersion ? { modelVersion } : {}),
    });
  },

  /**
   * Trigger a manual ML prediction for a specific box/chain.
   *
   * **This is a long-running operation** — takes 5-15 seconds depending
   * on the number of windows. Show a loading indicator to the user.
   *
   * @param request - Manual prediction request parameters
   * @returns Per-window prediction results and final aggregate
   *
   * @throws {AiPredictionApiError} 400 — Missing/invalid serialNo, invalid windows
   * @throws {AiPredictionApiError} 403 — Caller is not an IRES user
   * @throws {AiPredictionApiError} 404 — Chain or box not found
   * @throws {AiPredictionApiError} 422 — Chain not eligible
   * @throws {AiPredictionApiError} 500 — Prediction engine error
   */
  async runManualPrediction(
    request: ManualPredictRequest
  ): Promise<ManualPredictResponse> {
    return callAssist<ManualPredictResponse>(
      { operation: "runManualPrediction", body: request },
      MANUAL_PREDICTION_TIMEOUT_MS
    );
  },

  /**
   * Save a user correction for a specific window.
   *
   * Updates the chain summary on the backend to reflect the correction.
   *
   * @param request - Correction data including window, userId, and corrected values
   * @returns Confirmation with timestamp
   *
   * @throws {AiPredictionApiError} 400 — Missing/invalid fields
   * @throws {AiPredictionApiError} 403 — Caller is not an IRES user
   * @throws {AiPredictionApiError} 404 — Chain not found
   * @throws {AiPredictionApiError} 500 — Firestore write failed
   */
  async saveCorrection(
    request: SaveCorrectionRequest
  ): Promise<SaveCorrectionResponse> {
    return callAssist<SaveCorrectionResponse>({
      operation: "saveCorrection",
      body: request,
    });
  },

  /**
   * Load correction data for a chain.
   *
   * Returns empty corrections map if none exist (never 404).
   *
   * @param chainId - The program chain ID
   * @param serialNo - ComBox serial number
   * @returns Correction document with per-window corrections and final result
   *
   * @throws {AiPredictionApiError} 400 — Missing serialNo
   * @throws {AiPredictionApiError} 403 — Caller is not an IRES user
   */
  async getCorrections(
    chainId: string,
    serialNo: string
  ): Promise<CorrectionDocument> {
    return callAssist<CorrectionDocument>({
      operation: "getCorrections",
      chainId,
      serialNo,
    });
  },

  // ─── Backfill ──────────────────────────────────────────────────────────

  /**
   * Start a backfill job to generate predictions for all historical days.
   *
   * Returns immediately with a job ID. Use {@link getBackfillStatus} to poll progress.
   *
   * @param request - Chain and serial number to backfill
   * @returns Job status with `jobId` for polling
   *
   * @throws {AiPredictionApiError} 400 — Missing serialNo or chainId
   * @throws {AiPredictionApiError} 403 — Caller is not an IRES user
   * @throws {AiPredictionApiError} 404 — Chain not found
   * @throws {AiPredictionApiError} 409 — Backfill already running for this chain
   * @throws {AiPredictionApiError} 422 — Chain not eligible (no runs or unsupported program)
   */
  async startBackfill(request: BackfillRequest): Promise<BackfillResponse> {
    return callAssist<BackfillResponse>({
      operation: "startBackfill",
      body: request,
    });
  },

  /**
   * Start client-level activation backfill orchestration after ASSIST is enabled.
   *
   * Returns immediately with a job ID when orchestration starts, or `already_running`
   * when the backend is already processing activation for this client.
   *
   * @param request - Client activation backfill parameters
   * @returns Activation orchestration status and optional job ID
   *
   * @throws {AiPredictionApiError} 400 — Missing clientId or invalid scope
   * @throws {AiPredictionApiError} 403 — Caller is not an IRES user, or the feature is not
   *   activated for the resolved client
   */
  async startActivationBackfill(
    request: ActivationBackfillRequest
  ): Promise<ActivationBackfillResponse> {
    return callAssist<ActivationBackfillResponse>({
      operation: "startActivationBackfill",
      body: request,
    });
  },

  /**
   * Poll the status of a running backfill job.
   *
   * @param jobId - Job ID returned by {@link startBackfill}
   * @returns Current job status with progress counters
   *
   * @throws {AiPredictionApiError} 403 — Caller is not an IRES user
   * @throws {AiPredictionApiError} 404 — Job not found
   */
  async getBackfillStatus(jobId: string): Promise<BackfillResponse> {
    return callAssist<BackfillResponse>({
      operation: "getBackfillStatus",
      jobId,
    });
  },
};
